import { HttpErrorResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { MatSnackBar } from '@angular/material/snack-bar';
import { Router } from '@angular/router';
import { GlobalMessageOpenerService } from '@libraries/component-library';
import { GoogleAnalyticsService } from '@libraries/google-analytics';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { select, Store } from '@ngrx/store';
import { of } from 'rxjs';
import { catchError, filter, map, switchMap, tap, withLatestFrom } from 'rxjs/operators';
import { calculationParams, companyPin, selectUrl } from '../../+state/selectors';
import { CalculatorService } from '../../services/calculator.service';
import * as CalculatorActions from '../actions';
import { calculateMonthlyCost } from '../actions';

@Injectable()
export class AppEffects {
  displayError$ = createEffect(
    () => {
      return this.actions$.pipe(
        ofType(CalculatorActions.displayError),
        tap(({ error }) => {
          const type = 'warn';
          let body: string;
          let action: string;
          let errors: string[] = [];
          let onAction: () => any;

          // error handler here for no error;
          switch (error && error.status) {
            case 500:
              body = `Uh, oh! There was a server error while submitting your information.
                 Please try again or give us a call to assist you.`;
              action = 'CLOSE';
              break;
            case 400:
              if (error.error?.error) {
                this.snackBar.open(error.error.error, 'Close', { duration: 3000 });

                body = error.error.error;
              } else {
                body = 'We found some errors. Please fix them and try again.';
                errors = error.error ? Object.values(error.error) : errors;
                this.snackBar.open(body, 'Close', { duration: 3000 });
              }
              action = 'CLOSE';
              break;
            case 422:
              body = `We found some errors. Please fix them and try again.`;
              errors = error.error ? Object.values(error.error) : errors;
              action = 'CLOSE';
              break;
            default:
              body = `Uh, oh! Our servers are unreachable. Please try reloading the page to resolve the issue.
                 Also, you are always welcome to call us and complete over the phone.`;
              action = 'RELOAD PAGE';
              onAction = () => location && location.reload();
          }
          this.globalMessages.open({ body, errors, action, type, onAction });
        })
      );
    },
    { dispatch: false }
  );

  validatePin$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(CalculatorActions.validatePin),
      switchMap((action) => {
        return this.svc.validatePin(action.payload).pipe(
          map((resp) => {
            if (action.closeMenu) action.closeMenu();
            return CalculatorActions.validatePinSuccess({
              // TODO: switch to use analytics lib -- temporarily placing here until we complete `YT-13213: Integrate Routing/NgRx with Monorepo Google Analytics`
              fromMenu: action.fromMenu,
            });
          }),
          catchError((error: HttpErrorResponse) => {
            return of(CalculatorActions.validatePinFail({ error }));
          })
        );
      })
    );
  });

  validatePinSuccess$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(CalculatorActions.validatePinSuccess),
      withLatestFrom(
        this.store.pipe(select(companyPin)),
        this.store.pipe(select(selectUrl)),
        this.store.pipe(select(calculationParams))
      ),
      tap(([action, pin]) => {
        // TODO: switch to use analytics lib -- temporarily placing here until we complete `YT-13213: Integrate Routing/NgRx with Monorepo Google Analytics`
        this.analyticsService.setUserId(pin);
        this.analyticsService.setDimension('dimension1', pin);
        if (action.fromMenu) {
          this.analyticsService.sendEvent('general', 'signInFromMenu');
        }

        this.svc.storePinInCookie(pin);
      }),
      filter(([action, pin, url]) => url === '/eligible'),
      map(([action, pin, url, params]) => calculateMonthlyCost({ payload: params }))
    );
  });

  validatePinFail$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(CalculatorActions.validatePinFail),
      map(({ error }) => {
        return CalculatorActions.displayError({ error });
      })
    );
  });

  calcuateMonthlyCost$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(CalculatorActions.calculateMonthlyCost),
      switchMap(({ payload }) => {
        return this.svc.calculateMonthlyCost(payload).pipe(
          map((resp) =>
            CalculatorActions.calculateMonthlyCostSuccess({
              monthlyCostData: resp,
            })
          ),
          catchError((error: HttpErrorResponse) =>
            of(CalculatorActions.calculateMonthlyCostFail({ error }))
          )
        );
      })
    );
  });

  calculateMonthlyCostSuccess$ = createEffect(
    () => {
      return this.actions$.pipe(
        ofType(CalculatorActions.calculateMonthlyCostSuccess),
        withLatestFrom(this.store.pipe(select(selectUrl))),
        tap(([action, url]) => {
          // TODO: switch to use analytics lib -- temporarily placing here until we complete `YT-13213: Integrate Routing/NgRx with Monorepo Google Analytics`
          if (url === '/eligible') {
            this.analyticsService.sendEvent('general', 'recalculateCost');
          }

          this.router.navigate(['/eligible'], { state: { bypassGuard: true } });
        })
      );
    },
    {
      dispatch: false,
    }
  );

  calculateMonthlyCostFailDisplay$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(CalculatorActions.calculateMonthlyCostFail),
      filter(({ error }) => error.error.error !== 'This address is not in the Ygrene program.'),
      map(({ error }) => CalculatorActions.displayError({ error }))
    );
  });

  calculateMonthlyCostFailRedirect$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(CalculatorActions.calculateMonthlyCostFail),
      filter(({ error }) => error.error.error === 'This address is not in the Ygrene program.'),
      map(() => CalculatorActions.navigateToNotEligible())
    );
  });

  invalidPinFail$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(CalculatorActions.validatePinFail, CalculatorActions.calculateMonthlyCostFail),
      filter(({ error }) => error.error.error === 'Company pin is not valid'),
      filter(() => this.svc.cookieHasPin()),
      map(() => CalculatorActions.logout())
    );
  });

  navigateToNotEligible$ = createEffect(
    () => {
      return this.actions$.pipe(
        ofType(CalculatorActions.navigateToNotEligible),
        tap(() => {
          this.router.navigate(['/not-eligible'], {
            state: { bypassGuard: true },
          });
        })
      );
    },
    { dispatch: false }
  );

  logout$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(CalculatorActions.logout),
      switchMap(() => {
        return this.svc.logout().pipe(map(() => CalculatorActions.logoutSuccess()));
      })
    );
  });

  navigateToHome$ = createEffect(
    () => {
      return this.actions$.pipe(
        ofType(CalculatorActions.logoutSuccess, CalculatorActions.checkAnotherProperty),
        tap(() => {
          this.router.navigate(['/']);
        })
      );
    },
    { dispatch: false }
  );

  // TODO: switch to use analytics lib -- temporarily placing here until we complete `YT-13213: Integrate Routing/NgRx with Monorepo Google Analytics`
  checkAnotherProperty$ = createEffect(
    () => {
      return this.actions$.pipe(
        ofType(CalculatorActions.checkAnotherProperty),
        tap(() => {
          this.analyticsService.sendEvent('general', 'checkAnotherProperty');
        })
      );
    },
    { dispatch: false }
  );

  constructor(
    private router: Router,
    private actions$: Actions,
    private store: Store,
    private svc: CalculatorService,
    private globalMessages: GlobalMessageOpenerService,
    private analyticsService: GoogleAnalyticsService,
    private snackBar: MatSnackBar
  ) {}
}
